<template>
  <qtm-table :headers="headers">
    <template v-if="!disabled && !editOnly" v-slot:header.item-number>
      <div class="d-flex justify-space-between align-center">
        <div />
        <v-checkbox
          base-color="mid-light-grey"
          class="ms-4"
          color="interactive"
          density="compact"
          hide-details
          :indeterminate="someItemsSelected"
          :model-value="allItemsSelected"
          @update:model-value="toggleSelected"
        />
        #
      </div>
    </template>
    <draggable
      :list="items"
      animation="150"
      class="qtm-body"
      ghost-class="ghost"
      handle=".handle"
      item-key="id"
      tag="tbody"
      @start="onDragStart"
      @end="onDragEnd"
      @change="$emit('sort:items', $event)"
    >
      <template v-slot:item="{ element: item, index }">
        <cart-items-row
          ref="rows"
          :autofocus="autofocus && index === items.length - 1"
          :boms="boms"
          :cost-codes="costCodes"
          :disabled="disabled"
          :force-cost-code-select="forceCostCodeSelect"
          :edit-only="editOnly"
          :equipment="equipment"
          :include-units-column-in-rentals="includeUnitsColumnInRentals"
          :indexed="indexed"
          :item="item"
          :item-number-field="itemNumberField"
          :rearrangeable="rearrangeable && items.length > 1"
          :reference-items="referenceItems"
          :rental="rental"
          :require-cost-codes="requireCostCodes"
          :selected="selected"
          :taxes="taxes"
          :transformable="transformable"
          :units="units"
          :validate-price="validatePrice"
          :validate-units="validateUnits"
          :with-cost-code="withCostCodes"
          :with-prices="withPrices"
          @cost-code-changed="$emit('cost-code-changed', $event)"
          @insert="$emit('insert', { item, location: $event })"
          @tax-changed="$emit('tax-changed', $event)"
          @remove="$emit('remove', $event)"
          @update:selected="$emit('update:selected', $event)"
        >
          <template v-slot:actions="attrs">
            <slot name="actions" v-bind="attrs" />
          </template>
        </cart-items-row>
      </template>
    </draggable>
    <template v-slot:empty>
      <div>
        <!-- Remove empty state -->
      </div>
    </template>
  </qtm-table>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'
import type { Equipment, LineItem } from '@quotetome/materials-api'
import CartItemsRow from '@/components/orders/cart-items-row.vue'
import QtmTable from '@/components/qtm-table.vue'

export interface Props {
  autofocus?: boolean
  boms?: any[]
  costCodes?: any[]
  descriptionHeader?: string
  disabled?: boolean
  editOnly?: boolean
  equipment?: Equipment[]
  forceCostCodeSelect?: boolean
  indexed?: boolean
  itemNumberField?: string
  items: any[]
  rearrangeable?: boolean
  referenceItems?: LineItem[]
  rental?: boolean
  requireCostCodes?: boolean
  selected?: (number | string)[]
  taxes?: any[]
  transformable?: boolean
  units?: any[]
  validatePrice?: boolean
  validateUnits?: boolean
  withCostCodes?: boolean
  withPrices?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  boms: () => [],
  costCodes: () => [],
  descriptionHeader: undefined,
  equipment: () => [],
  indexed: true,
  itemNumberField: 'index',
  referenceItems: () => [],
  selected: () => [],
  taxes: () => [],
  transformable: true,
  units: () => [],
  validatePrice: true,
})
const emit = defineEmits(['cost-code-changed', 'insert', 'remove', 'sort:items', 'tax-changed', 'update:selected'])

const headers = computed(() => {
  let headerArr = []

  if (props.indexed) {
    headerArr.push({ title: '#', cellClass: 'text-right', value: 'item-number' })
  }

  headerArr.push({ title: props.descriptionHeader || (props.rental ? 'Rental items' : 'Purchase items') })

  if (props.equipment.length > 0) {
    headerArr.push({ title: 'Equipment' })
  }

  headerArr = headerArr.concat([
    { title: 'Qty' },
    props.rental ? { title: 'Duration', colspan: 2 } : { title: 'U/M' },
  ])

  if (props.rental && includeUnitsColumnInRentals.value) {
    headerArr.push({ title: 'U/M' })
  }

  if (props.withPrices) {
    if (props.rental) {
      headerArr = headerArr.concat(['Day rate', 'Week rate', 'Month rate'].map(title => ({ title })))
    }
    else {
      headerArr.push({ title: 'Unit price' })
    }

    headerArr.push({ title: 'Total' })
  }

  if (props.withCostCodes) {
    headerArr.push({ title: 'Cost code' })
  }

  if (props.taxes.length) {
    headerArr.push({ title: 'Tax' })
  }

  headerArr.push({ title: 'Comment' })

  if (!props.disabled && !props.editOnly) {
    headerArr.push({ title: '' })
  }

  return headerArr.map(header => ({ ...header, value: header.value || header.title }))
})

const includeUnitsColumnInRentals = computed(() => {
  const hasItemsWithNoUnits = props.items.filter(item => !item.unit).length > 0

  return !(props.rental && props.validateUnits && units.length && hasItemsWithNoUnits)
})

const allItemsSelected = computed(() => props.items.every(item => props.selected.includes(item.id)))
const someItemsSelected = computed(() => {
  return !allItemsSelected.value && props.items.some(item => props.selected.includes(item.id))
})
const toggleSelected = () => {
  if (allItemsSelected.value) {
    emit('update:selected', props.selected.filter(id => !props.items.some(item => item.id === id)))
  }
  else {
    const selected = new Set(props.selected)

    props.items.forEach(item => {
      if (!selected.has(item.id)) {
        selected.add(item.id)
      }
    })

    emit('update:selected', Array.from(selected))
  }

}

const rows = ref<InstanceType<typeof CartItemsRow>[]>()
const isValid = () => !rows.value || !rows.value.length || rows.value.every(row => row.isValid())
const onDragStart = (event: any) => {
  const parent = event.item.closest('.overflow-x-auto')

  parent?.classList.remove('overflow-x-auto')
  parent?.classList.add('overflow-x-hidden')
  parent?.classList.add('no-handle')
}
const onDragEnd = (event: any) => {
  const parent = event.item.closest('.overflow-x-hidden')

  parent?.classList.add('overflow-x-auto')
  parent?.classList.remove('overflow-x-hidden')
  parent?.classList.remove('no-handle')
}
const focusLastItem = () => rows.value?.focusDescription()

defineExpose({ focusLastItem, isValid })
</script>
